import { AlertTriangle, Check, CircleDashed, Loader, PauseCircle, RotateCcw, X } from "lucide-react";

import { Elapsed } from "./ui.jsx";

/**
 * The single source of truth for what a status looks like.
 *
 * Every status in the system resolves here, so a deploy that is "running" looks
 * the same in the overview, the timeline and the project page. Getting this wrong
 * once is worse than getting it wrong everywhere.
 */
const STATUS = {
    success: { label: "success", className: "badge-success", Icon: Check },
    running: { label: "running", className: "badge-running", Icon: Loader, spin: true },
    queued: { label: "queued", className: "badge-queued", Icon: CircleDashed },
    failed: { label: "failed", className: "badge-failed", Icon: X },
    "rolled-back": { label: "rolled back", className: "badge-warn", Icon: RotateCcw },
    cancelled: { label: "cancelled", className: "badge-queued", Icon: X },
    skipped: { label: "skipped", className: "badge-queued", Icon: CircleDashed },

    // PM2 process states.
    online: { label: "online", className: "badge-success", Icon: Check },
    stopped: { label: "stopped", className: "badge-queued", Icon: PauseCircle },
    errored: { label: "errored", className: "badge-failed", Icon: X },
    stopping: { label: "stopping", className: "badge-warn", Icon: Loader, spin: true },
    launching: { label: "launching", className: "badge-running", Icon: Loader, spin: true },

    // What the monitor says about a component: answering, not, or about to be said not.
    up: { label: "up", className: "badge-success", Icon: Check },
    down: { label: "down", className: "badge-failed", Icon: X },
    failing: { label: "missed once", className: "badge-warn", Icon: AlertTriangle },
    restarting: { label: "restarting", className: "badge-running", Icon: Loader, spin: true },
    paused: { label: "paused", className: "badge-queued", Icon: PauseCircle },
    unknown: { label: "not checked", className: "badge-queued", Icon: CircleDashed }
};

const UNKNOWN = { label: "unknown", className: "badge-warn", Icon: AlertTriangle };

/**
 * `since` is when a running thing started. Given one, a "running" pill counts
 * the seconds — here rather than at each call site, so a deploy in progress
 * reads the same in the overview, the table, the project header and the modal.
 */
export function StatusBadge({ status, since, children }) {
    const { label, className, Icon, spin } = STATUS[status] ?? { ...UNKNOWN, label: status ?? "unknown" };
    return (
        <span className={className}>
            <Icon size={12} className={spin ? "animate-spin" : undefined} aria-hidden />
            {children ?? label}
            {status === "running" && since ? <Elapsed since={since} /> : null}
        </span>
    );
}

/**
 * Just the glyph, in the status colour.
 *
 * For a phone row, where a full pill costs most of the line and says less than
 * the commit message it was crowding out.
 */
export function StatusIcon({ status, size = 15 }) {
    const { label, Icon, spin } = STATUS[status] ?? { ...UNKNOWN, label: status ?? "unknown" };
    const tone = {
        success: "text-green-600 dark:text-green-400",
        online: "text-green-600 dark:text-green-400",
        running: "text-blue-600 dark:text-blue-400",
        launching: "text-blue-600 dark:text-blue-400",
        stopping: "text-amber-600 dark:text-amber-400",
        failed: "text-red-600 dark:text-red-400",
        errored: "text-red-600 dark:text-red-400",
        "rolled-back": "text-amber-600 dark:text-amber-400",
        up: "text-green-600 dark:text-green-400",
        down: "text-red-600 dark:text-red-400",
        failing: "text-amber-600 dark:text-amber-400"
    }[status] ?? "text-gray-400 dark:text-gray-500";

    return (
        <Icon
            size={size}
            className={`shrink-0 ${tone} ${spin ? "animate-spin" : ""}`}
            aria-label={label}
            role="img"
        />
    );
}

/** A bare dot, for dense rows where a full badge would be noise. */
export function StatusDot({ status, title }) {
    const tone = {
        success: "bg-green-500",
        online: "bg-green-500",
        running: "bg-blue-500 animate-pulse",
        launching: "bg-blue-500 animate-pulse",
        queued: "bg-gray-400",
        stopped: "bg-gray-400",
        failed: "bg-red-500",
        errored: "bg-red-500",
        "rolled-back": "bg-amber-500",
        up: "bg-green-500",
        down: "bg-red-500",
        failing: "bg-amber-500",
        restarting: "bg-blue-500 animate-pulse",
        paused: "bg-gray-400"
    }[status] ?? "bg-gray-300 dark:bg-gray-600";

    return <span className={`inline-block size-2 shrink-0 rounded-full ${tone}`} title={title ?? status} />;
}
